import { pick, cond, always, inc, T, isNil } from 'ramda'

interface PaginationParams {
  page?: number
  size?: number
  sort?: string
  sortOrder?: string
}

interface ImmutableLike {
  toJS(): PaginationParams
}

type PaginationState = PaginationParams | ImmutableLike

/**
 * Creates pagination parameters by combining the current pagination state with
 * new input parameters. This is used to handle pagination and sorting across
 * multiple API requests. The page number is automatically incremented based on
 * the current state.
 *
 * @param {PaginationState} paginationState - Current pagination state, may be
 *   an Immutable.js object
 * @param {Partial<PaginationParams>} overrides - New query parameters to
 *   override existing state
 * @param {(keyof PaginationParams)[]} cachedKeys - Keys to pick from pagination
 *   state. If empty/null/undefined, all keys are used.
 * @returns {PaginationParams} Combined pagination parameters with incremented
 *   page number
 */
export const makePaginationParams = (
  paginationState: PaginationState = {},
  overrides: Partial<PaginationParams> = {},
  cachedKeys: Array<keyof PaginationParams> = [
    'page',
    'size',
    'sort',
    'sortOrder',
  ],
): PaginationParams => {
  const state = _isImmutableLike(paginationState)
    ? paginationState.toJS()
    : paginationState

  // If cachedKeys is empty/null/undefined, use all keys from state
  const stateParams = cachedKeys?.length ? pick(cachedKeys, state) : state
  const { page, ...otherParams } = stateParams

  return {
    page: cond([
      [isNil, always(0)],
      [T, inc],
    ])(page),
    ...otherParams,
    ...overrides,
  }
}

/**
 * Small helper method to make sure we're only operating with JS objects.
 *
 * @param obj An object that may or may not be an ImmutableJS map
 * @returns
 */
function _isImmutableLike(obj: any): obj is ImmutableLike {
  return obj && typeof obj.toJS === 'function'
}
